import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, ArrowsClockwise, Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function SavedSearches() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(null);

  const load = () => api.get("/saved-searches").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    try {
      await api.patch(`/saved-searches/${s.id}`, { enabled: !s.enabled });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setFreq = async (s, frequency) => {
    try {
      await api.patch(`/saved-searches/${s.id}`, { frequency });
      toast.success(`Frequency set to ${frequency}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setDigest = async (s, digest_mode) => {
    try {
      await api.patch(`/saved-searches/${s.id}`, { digest_mode });
      toast.success(`Email format: ${digest_mode === "per_finding" ? "one per grant" : "summary"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleSms = async (s) => {
    try {
      await api.patch(`/saved-searches/${s.id}`, { sms_enabled: !s.sms_enabled });
      toast.success(!s.sms_enabled ? "SMS alerts enabled for this search" : "SMS alerts disabled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const runOne = async (s) => {
    setRunning(s.id);
    try {
      const { data } = await api.post(`/saved-searches/${s.id}/run`);
      const newCount = (data.new_grants || []).length;
      if (data.first_run) {
        toast.success(`Baseline captured (${data.total_hits} current hits). Future alerts will report new postings.`);
      } else if (newCount === 0) {
        toast.message("No new grants since last run.");
      } else {
        toast.success(`${newCount} new grant${newCount === 1 ? "" : "s"} found.`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setRunning(null);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="gf-overline">Alerts</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Saved searches.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Save a search to receive an email when new matching grants are posted on Grants.gov.
        First "run" captures a baseline; subsequent runs alert you to <em>new</em> postings only.
      </p>

      <div className="mt-6 flex items-center gap-3">
        <Link to="/search">
          <Button data-testid="add-saved-from-search" className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
            <MagnifyingGlass size={16} className="mr-2" /> Search & save
          </Button>
        </Link>
      </div>

      <div className="mt-6 border border-zinc-200 bg-white" data-testid="saved-searches-table">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Bell size={32} className="mx-auto text-zinc-400" />
            <div className="gf-overline mt-3">No saved searches yet</div>
            <p className="text-zinc-600 mt-2 text-sm">
              Run a search and click "Save this search" to add one.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-zinc-200 text-left gf-overline">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Filters</th>
                <th className="px-6 py-3">Frequency</th>
                <th className="px-6 py-3">Digest</th>
                <th className="px-6 py-3">SMS</th>
                <th className="px-6 py-3">Last run</th>
                <th className="px-6 py-3">Enabled</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`saved-row-${s.id}`}>
                  <td className="px-6 py-4 font-semibold">{s.name}</td>
                  <td className="px-6 py-4 text-zinc-600 font-mono text-xs">
                    {s.keyword && <div>kw: {s.keyword}</div>}
                    {s.agencies?.length > 0 && <div>agencies: {s.agencies.join(", ")}</div>}
                    {s.statuses?.length > 0 && <div>statuses: {s.statuses.join(", ")}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <Select value={s.frequency} onValueChange={(v) => setFreq(s, v)}>
                      <SelectTrigger data-testid={`freq-${s.id}`} className="gf-sharp h-8 border-zinc-300 bg-white text-xs w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="gf-sharp">
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <Select value={s.digest_mode || "summary"} onValueChange={(v) => setDigest(s, v)}>
                      <SelectTrigger data-testid={`digest-${s.id}`} className="gf-sharp h-8 border-zinc-300 bg-white text-xs w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="gf-sharp">
                        <SelectItem value="summary">Rolled-up summary</SelectItem>
                        <SelectItem value="per_finding">One per grant</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleSms(s)}
                      data-testid={`sms-${s.id}`}
                      className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 font-mono ${s.sms_enabled ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200"}`}
                    >
                      {s.sms_enabled ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-zinc-500 font-mono text-xs">
                    {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <Switch
                      checked={s.enabled}
                      onCheckedChange={() => toggle(s)}
                      data-testid={`enabled-${s.id}`}
                    />
                  </td>
                  <td className="px-6 py-4 text-right space-x-3 whitespace-nowrap">
                    <button
                      onClick={() => runOne(s)}
                      disabled={running === s.id}
                      data-testid={`run-${s.id}`}
                      className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs uppercase tracking-wide font-bold"
                    >
                      <ArrowsClockwise size={14} /> {running === s.id ? "Running…" : "Run now"}
                    </button>
                    <button
                      onClick={() => remove(s)}
                      data-testid={`delete-${s.id}`}
                      className="inline-flex items-center gap-1 text-red-700 hover:underline text-xs uppercase tracking-wide font-bold"
                    >
                      <Trash size={14} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
